import {
  AddressBookIcon,
  HourglassIcon,
  ReceiptIcon,
  CoinsIcon,
  BriefcaseIcon,
  FlagIcon,
  FileImportIcon,
  UserPlusIcon,
  FileTextIcon,
  BuildingIcon,
  UsersIcon,
  ShieldLockIcon,
  ClipboardListIcon,
  CalendarIcon,
  ChartPieIcon,
  ChartBarIcon,
  UserCheckIcon,
  ListCheckIcon,
  ActivityIcon,
  PencilIcon,
  CalculatorIcon,
  ReportMoneyIcon,
  RefreshIcon,
  KeyIcon,
  CalendarStatsIcon,
  ScaleIcon,
  LayoutListIcon,
  ClipboardCheckIcon,
  LinkIcon,
  BellIcon,
  ArrowBackUpIcon,
  CalendarDollarIcon,
  SubtaskIcon,
  NotebookIcon,
  type IconProps,
} from '../ui';
import { COMPANY_SCOPE_ROLES, type AppRole } from '../types/auth';

// The navigation model, kept out of Sidebar.tsx so it can be imported without dragging a
// component along — navGuardParity.test.tsx reads NAV_GROUPS to prove this table and the
// router's RequireRole groups admit exactly the same roles for the same paths (Slice P0-H B4).
// Sidebar remains its only renderer.

// Grouped navigation hierarchy. Sections grow per business slice; group headers give the
// premium enterprise structure (operations vs. commission vs. …). Items may declare the
// roles allowed to see them (role-aware nav) — mirrors the route guards so a sales_manager
// only sees the commission surfaces they can actually open (D4 self-service).
//
// `icon` is REQUIRED (not `icon?`): a compile error on a missing icon is the point — six months
// from now, whoever adds the next nav item won't remember this convention on their own, so the
// type system carries the rule instead of a code-review habit. Decorative-only (the shared
// icon-foundation `Svg` wrapper already sets aria-hidden/focusable="false" by default); the
// link's own text remains the sole accessible name.
export interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly icon: (props: IconProps) => JSX.Element;
  readonly end?: boolean;
  readonly roles?: readonly AppRole[];
}
export interface NavGroup {
  readonly title: string;
  readonly items: readonly NavItem[];
}

const COMPANY_COMMISSION: readonly AppRole[] = ['administrator', 'finance_manager', 'management'];
// Mirrors router.tsx COMMISSION_SELF_SERVICE_ROLES — the RequireRole guard shared by
// /commission, /commission/analytics and (since Slice P0-H / D-155) /commission/settlement.
const SELF_SERVICE_COMMISSION: readonly AppRole[] = ['administrator', 'finance_manager', 'management', 'sales_manager'];
// Mirrors router.tsx COMPANY_REPORT_ROLES — the cross-manager review surfaces (/managers,
// /exceptions) are everyone EXCEPT sales_manager. Same COMPANY_SCOPE_ROLES constant the router
// guard uses, so the two can never drift apart by editing only one of them.
const COMPANY_REPORTS: readonly AppRole[] = COMPANY_SCOPE_ROLES;
const ADMIN_ONLY: readonly AppRole[] = ['administrator'];
// Mirrors router.tsx's PLATFORM_ADMIN_ROLES — /admin/companies carries member deactivation, which
// 0135 §3 opened to management alongside administrator.
const PLATFORM_ADMIN: readonly AppRole[] = ['administrator', 'management'];
// Mirrors router.tsx IMPORT_ROLES (fn_import_authorize, 0072/0073 admits only these two roles).
const IMPORT_ONLY: readonly AppRole[] = ['administrator', 'finance_manager'];
// Mirrors router.tsx CUSTOMER_ROLES (fn_customer_create, 0098, reuses fn_payment_authorize_write's
// administrator|finance_manager set) — Slice P1-A.
const CUSTOMER_ENTRY: readonly AppRole[] = ['administrator', 'finance_manager'];
// Mirrors router.tsx PAYMENT_POLICY_ROLES (fn_payment_authorize_write, 0082/0083).
const PAYMENT_POLICY_ONLY: readonly AppRole[] = ['administrator', 'finance_manager'];
// Mirrors router.tsx OPENING_ROLES (opening-balance RPCs, 0102, reuse fn_payment_authorize_write's
// administrator|finance_manager set) — Slice P3.
const OPENING_ENTRY: readonly AppRole[] = ['administrator', 'finance_manager'];
// Mirrors router.tsx REFUND_ROLES (fn_refund_list 0139 / the execute_refund Edge action, both via
// fn_payment_authorize_write's administrator|finance_manager set) — D-210.
const REFUND_ENTRY: readonly AppRole[] = ['administrator', 'finance_manager'];
// Mirrors router.tsx NOTEBOOK_ROLES (fn_notebook_authorize_write, 0146) — D-223.
const NOTEBOOK_ENTRY: readonly AppRole[] = ['administrator', 'management'];

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    title: 'ოპერაციები',
    items: [
      { to: '/debtors', label: 'დებიტორები', icon: AddressBookIcon },
      { to: '/aging', label: 'ვადების ანალიზი', icon: HourglassIcon },
      { to: '/payments', label: 'გადახდები', icon: ReceiptIcon },
      { to: '/advances', label: 'ავანსები', icon: CoinsIcon },
      { to: '/managers', label: 'მენეჯერები', icon: BriefcaseIcon, roles: COMPANY_REPORTS },
      { to: '/exceptions', label: 'გამონაკლისები', icon: FlagIcon, roles: COMPANY_REPORTS },
      // Phase 7 Slice 7.11-B (D-AE): no `roles` restriction, same as debtors/aging/payments/
      // advances above — fn_alert_task_list/_refresh (0094) admit every APP_ROLES role
      // (sales_manager scoped, the rest company-wide), so there is nothing narrower to gate here.
      { to: '/alerts', label: 'შეტყობინებები', icon: BellIcon },
      // D-218: no `roles` restriction for the same reason — fn_promise_summary (0144) admits every
      // APP_ROLES role and scopes a sales_manager to their own customers itself.
      { to: '/promises', label: 'გადახდის დაპირებები', icon: CalendarDollarIcon },
      // D-219: no `roles` restriction for the same reason — fn_internal_task_list (0145) admits
      // every APP_ROLES role and shows a sales_manager only the tasks assigned to them.
      { to: '/tasks', label: 'შიდა დავალებები', icon: SubtaskIcon },
      // D-223: the Management Notebook — administrator | management only (A9), unlike the two
      // Phase-8 entries above; a sales_manager never sees this link.
      { to: '/notebook', label: 'შეგროვების მიზნები', icon: NotebookIcon, roles: NOTEBOOK_ENTRY },
      // Slice P1-A: the customer registry sits with the other data-intake surfaces (import,
      // payment terms) in ოპერაციები — ადმინისტრირება stayed administrator-only in P0-H and
      // finance_manager must see this.
      { to: '/customers', label: 'კლიენტები', icon: UserPlusIcon, roles: CUSTOMER_ENTRY },
      // Slice P3: opening balances sit with the other data-intake surfaces (customers, import) in
      // ოპერაციები — it writes financial data, not configuration, and finance_manager must see it
      // (ადმინისტრირება stayed administrator-only in P0-H).
      { to: '/opening-balances', label: 'საწყისი ნაშთები', icon: ScaleIcon, roles: OPENING_ENTRY },
      // D-210: money leaving the company sits with the other financial-write surfaces in
      // ოპერაციები. The refund itself is started from a client's page; this is the history.
      { to: '/refunds', label: 'თანხის დაბრუნებები', icon: ArrowBackUpIcon, roles: REFUND_ENTRY },
      { to: '/import', label: 'იმპორტი', icon: FileImportIcon, roles: IMPORT_ONLY },
      { to: '/admin/payment-policies', label: 'გადახდის პირობები', icon: FileTextIcon, roles: PAYMENT_POLICY_ONLY },
      { to: '/admin/installment-templates', label: 'განვადების შაბლონები', icon: LayoutListIcon, roles: PAYMENT_POLICY_ONLY },
      // Short singular form, not the page's own plural "დავალიანების გრაფიკები" (PlansPage.tsx) —
      // the full plural wraps to two lines in the sidebar, the only nav item that does; every
      // sibling item here stays one line. "დავალიანების გრაფიკი" is the exact fallback the task
      // itself named for a narrow spot; verified live at 375px that it now fits on one line.
      { to: '/admin/recovery-plans', label: 'დავალიანების გრაფიკი', icon: ClipboardCheckIcon, roles: PAYMENT_POLICY_ONLY },
      { to: '/admin/payment-matching', label: 'გადახდის მიბმა', icon: LinkIcon, roles: PAYMENT_POLICY_ONLY },
    ],
  },
  {
    title: 'საკომისიოები',
    items: [
      { to: '/commission', label: 'მიმოხილვა', icon: ChartPieIcon, end: true, roles: SELF_SERVICE_COMMISSION },
      { to: '/commission/analytics', label: 'ანალიტიკა', icon: ChartBarIcon, roles: SELF_SERVICE_COMMISSION },
      { to: '/commission/recipients', label: 'მიმღებები', icon: UserCheckIcon, roles: COMPANY_COMMISSION },
      { to: '/commission/rules', label: 'წესები', icon: ListCheckIcon, roles: COMPANY_COMMISSION },
      { to: '/commission/events', label: 'მოვლენები', icon: ActivityIcon, roles: COMPANY_COMMISSION },
      { to: '/commission/adjustments', label: 'კორექციები', icon: PencilIcon, roles: COMPANY_COMMISSION },
      { to: '/commission/overrides', label: 'ბაზის ხელით შესწორება', icon: CalculatorIcon, roles: COMPANY_COMMISSION },
      { to: '/commission/settlement', label: 'ანგარიშსწორება', icon: ReportMoneyIcon, roles: SELF_SERVICE_COMMISSION },
      // K-M (D-184): one entry for every commission role — the RPC scopes the CONTENT itself
      // (own figures for a sales_manager, a ჩემი/ყველა switch for the company-wide roles).
      { to: '/commission/monthly', label: 'თვის საკომისიო', icon: CalendarStatsIcon, roles: SELF_SERVICE_COMMISSION },
      // K-D-UI (D-183): administrator-only, matching fn_commission_backfill_authorize (0109:88)
      // — the only /commission entry narrower than COMPANY_COMMISSION.
      { to: '/commission/backfill', label: 'საკომისიოს გადათვლა', icon: RefreshIcon, roles: ADMIN_ONLY },
    ],
  },
  {
    title: 'ადმინისტრირება',
    items: [
      { to: '/admin', label: 'კომპანია', icon: BuildingIcon, end: true, roles: ADMIN_ONLY },
      { to: '/admin/users', label: 'მომხმარებლები', icon: UsersIcon, roles: ADMIN_ONLY },
      // Not ADMIN_ONLY: member deactivation on this page is management's too (0135 §3). The
      // provisioning sections inside gate themselves on the administrator role.
      { to: '/admin/companies', label: 'კომპანიები და ანგარიშები', icon: KeyIcon, roles: PLATFORM_ADMIN },
      { to: '/admin/roles', label: 'როლები და უფლებები', icon: ShieldLockIcon, roles: ADMIN_ONLY },
      { to: '/admin/audit', label: 'აუდიტი', icon: ClipboardListIcon, roles: ADMIN_ONLY },
      { to: '/admin/calendar', label: 'ბიზნეს-კალენდარი', icon: CalendarIcon, roles: ADMIN_ONLY },
    ],
  },
];
